import { useEffect, useRef } from "react";

/*
  FluidBackground
  ----------------
  Calm black "oily water" WebGL background with a midnight-blue snake trail
  that follows the pointer. Idle auto-roam when no user input.
*/

const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const NOISE = `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}
float snoise(vec2 v){
  const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
  vec2 i=floor(v+dot(v,C.yy));
  vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
  vec4 x12=x0.xyxy+C.xxzz; x12.xy-=i1;
  i=mod289(i);
  vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
  vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
  m=m*m; m=m*m;
  vec3 x=2.0*fract(p*C.www)-1.0;
  vec3 h=abs(x)-0.5;
  vec3 ox=floor(x+0.5);
  vec3 a0=x-ox;
  m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
  vec3 g;
  g.x=a0.x*x0.x+h.x*x0.y;
  g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.0*dot(m,g);
}
float fbm(vec2 p){
  float v=0.0; float a=0.5;
  for(int i=0;i<4;i++){ v+=a*snoise(p); p=p*1.8+vec2(1.7,9.2); a*=0.5; }
  return v;
}
`;

const TRAIL_FRAG = `
precision highp float;
varying vec2 v_uv;
uniform sampler2D u_prev;
uniform vec2  u_mouse;
uniform vec2  u_pmouse;
uniform float u_active;
uniform float u_time;
uniform float u_aspect;
${NOISE}
float sdSeg(vec2 p, vec2 a, vec2 b){
  vec2 pa=p-a, ba=b-a;
  float h=clamp(dot(pa,ba)/max(dot(ba,ba),1e-6),0.0,1.0);
  return length(pa-ba*h);
}
void main(){
  vec2 uv=v_uv;
  // advect by the flow field
  vec2 fp=uv*2.4+u_time*0.03;
  vec2 flow=vec2(snoise(fp), snoise(fp+vec2(31.4,7.7)));
  vec2 suv=uv - flow*0.003;
  float prev=texture2D(u_prev, suv).r * 0.975;

  // deposit brush along pointer movement
  vec2 p=vec2(uv.x*u_aspect, uv.y);
  vec2 a=vec2(u_pmouse.x*u_aspect, u_pmouse.y);
  vec2 b=vec2(u_mouse.x*u_aspect, u_mouse.y);
  float d=sdSeg(p,a,b);
  float brush=smoothstep(0.05,0.0,d)*u_active*1.0;

  float v=max(prev, brush);
  v=clamp(v,0.0,1.0);
  gl_FragColor=vec4(v,v,v,1.0);
}
`;

const DISPLAY_FRAG = `
precision highp float;
varying vec2 v_uv;
uniform sampler2D u_trail;
uniform float u_time;
uniform float u_aspect;
${NOISE}
void main(){
  vec2 uv=v_uv;
  vec2 p=vec2((uv.x-0.5)*u_aspect, uv.y-0.5)*1.8;
  float t=texture2D(u_trail, uv).r;

  float tm=u_time*0.035;
  vec2 q=vec2(fbm(p+vec2(0.0,tm)), fbm(p+vec2(5.2,1.3)-tm));
  q += (texture2D(u_trail, uv+vec2(0.004,0.0)).r - texture2D(u_trail, uv-vec2(0.004,0.0)).r) * 1.2;
  vec2 r=vec2(fbm(p+1.8*q+vec2(1.7,9.2)+tm*0.6), fbm(p+1.8*q+vec2(8.3,2.8)));
  float f=fbm(p+2.2*r);

  vec3 deep=vec3(0.015,0.017,0.028);
  vec3 low =vec3(0.035,0.040,0.070);
  vec3 col=mix(deep, low, smoothstep(-0.6,0.9,f));
  float vein=smoothstep(0.35,0.75,length(r));
  col+=vec3(0.03,0.035,0.075)*vein*0.2;

vec3 midnight=vec3(0.08,0.13,0.48);
vec3 core=vec3(0.28,0.39,0.85);
float glow=smoothstep(0.02,0.88,t);
float hot =pow(t,3.0);
col+=midnight*glow*0.9;
col+=core*hot*0.8;
  col+=low*glow*(0.6+0.6*f);

  // vignette
  float d=length(uv-0.5);
  col*=1.0-smoothstep(0.45,1.05,d)*0.85;

  // grain
  float g=fract(sin(dot(gl_FragCoord.xy,vec2(12.9898,78.233)))*43758.5453);
  col+=(g-0.5)*0.002;

  gl_FragColor=vec4(col,1.0);
}
`;

function compile(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}

function program(gl, vsrc, fsrc) {
  const p = gl.createProgram();
  const vs = compile(gl, gl.VERTEX_SHADER, vsrc);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsrc);
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.bindAttribLocation(p, 0, "a_pos");
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(p));
    return null;
  }
  return p;
}

export const FluidBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl =
      canvas.getContext("webgl", { antialias: false, alpha: false }) ||
      canvas.getContext("experimental-webgl");
    if (!gl) {
      canvas.style.background = "#05060a";
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resScale = 0.85;

    const displayProg = program(gl, VERT, DISPLAY_FRAG);
    const trailProg = program(gl, VERT, TRAIL_FRAG);

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    let W = 0, H = 0;
    let fboA, fboB, texA, texB;

    const makeTarget = () => {
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, W, H, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      return { tex, fbo };
    };

    const resize = () => {
      const cw = window.innerWidth;
      const ch = window.innerHeight;
      canvas.style.width = cw + "px";
      canvas.style.height = ch + "px";
      W = Math.max(2, Math.floor(cw * dpr * resScale));
      H = Math.max(2, Math.floor(ch * dpr * resScale));
      canvas.width = W;
      canvas.height = H;
      const a = makeTarget();
      const b = makeTarget();
      texA = a.tex; fboA = a.fbo;
      texB = b.tex; fboB = b.fbo;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fboA);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fboB);
      gl.clear(gl.COLOR_BUFFER_BIT);
    };
    resize();
    window.addEventListener("resize", resize);

    const mouse = { x: 0.5, y: 0.5 };
    const pmouse = { x: 0.5, y: 0.5 };
    let active = 0;
    let lastInput = -9999; // idle auto-roam on load

    const setFromClient = (cx, cy) => {
      mouse.x = cx / window.innerWidth;
      mouse.y = 1 - cy / window.innerHeight;
      active = 1;
      lastInput = performance.now();
    };
    const onMove = (e) => setFromClient(e.clientX, e.clientY);
    const onTouch = (e) => {
      if (e.touches && e.touches[0])
        setFromClient(e.touches[0].clientX, e.touches[0].clientY);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onTouch, { passive: true });
    window.addEventListener("touchstart", onTouch, { passive: true });

    const uT = {
      prev: gl.getUniformLocation(trailProg, "u_prev"),
      mouse: gl.getUniformLocation(trailProg, "u_mouse"),
      pmouse: gl.getUniformLocation(trailProg, "u_pmouse"),
      active: gl.getUniformLocation(trailProg, "u_active"),
      time: gl.getUniformLocation(trailProg, "u_time"),
      aspect: gl.getUniformLocation(trailProg, "u_aspect"),
    };
    const uD = {
      trail: gl.getUniformLocation(displayProg, "u_trail"),
      time: gl.getUniformLocation(displayProg, "u_time"),
      aspect: gl.getUniformLocation(displayProg, "u_aspect"),
    };

    const start = performance.now();
    let raf;
    const render = () => {
      const now = performance.now();
      const time = (now - start) / 1000;
      const aspect = W / H;

      // idle auto-roam
      if (now - lastInput > 1500) {
        const s = time * 0.55;
        mouse.x = 0.5 + 0.34 * Math.sin(s) * Math.cos(s * 0.37);
        mouse.y = 0.5 + 0.3 * Math.sin(s * 0.8 + 1.3);
        active = 1;
      } else if (now - lastInput > 140) {
        active = 0;
      }

      pmouse.x += (mouse.x - pmouse.x) * 0.18;
      pmouse.y += (mouse.y - pmouse.y) * 0.18;

      // trail pass
      gl.bindFramebuffer(gl.FRAMEBUFFER, fboB);
      gl.viewport(0, 0, W, H);
      gl.useProgram(trailProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texA);
      gl.uniform1i(uT.prev, 0);
      gl.uniform2f(uT.mouse, mouse.x, mouse.y);
      gl.uniform2f(uT.pmouse, pmouse.x, pmouse.y);
      gl.uniform1f(uT.active, active);
      gl.uniform1f(uT.time, time);
      gl.uniform1f(uT.aspect, aspect);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      // swap
      let tmpT = texA, tmpF = fboA;
      texA = texB; fboA = fboB;
      texB = tmpT; fboB = tmpF;

      // display pass
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, W, H);
      gl.useProgram(displayProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texA);
      gl.uniform1i(uD.trail, 0);
      gl.uniform1f(uD.time, time);
      gl.uniform1f(uD.aspect, aspect);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("touchstart", onTouch);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "block",
        zIndex: 1,
      }}
    />
  );
};

export default FluidBackground;